/* eslint-disable no-unused-vars */
import React, { FC, memo, useState } from 'react';
import {
  Card,
  makeStyles,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Table,
  Box,
  colors,
  Tooltip
} from '@material-ui/core';
import {
  QuotationPriceCell,
  QuotationProductNameCell,
  QuotationQuantityCell,
  QuotationTotalCell,
  QuotationTypeCell
} from './table';
import { QuotationTotalPrice } from './QuotationTotalPrice';
import { AlternatingColorTableRow } from 'src/components';
import useQuotationProduct from 'src/hooks/quotation-v2/use-quotation-products';
import MenuItemQuote from './table/MenuItemQuote';
import TrendingUpIcon from '@material-ui/icons/TrendingUp';
import ReportProblemIcon from '@material-ui/icons/ReportProblem';

const useStyles = makeStyles(() => ({
  root: {},
  table: {
    borderTop: 'black solid 2px',
    backgroundColor: 'transparent',
    '& th, & td': {
      backgroundColor: 'transparent'
    }
  },
  amount: { fontWeight: 500 },
  headerTypeCell: {
    fontWeight: 700,
    fontSize: '18px',
    textAlign: 'left',
    padding: 0
  }
}));

interface Props {
  isScreenCapture: boolean;
}

const Component: FC<Props> = ({ isScreenCapture = false }) => {
  const classes = useStyles();
  const {
    quotationData,
    quotationParams,
    onChangeCustomPrice
  } = useQuotationProduct();

  const capturingScreen = quotationParams?.is_srp && !isScreenCapture;

  return (
    <Card elevation={0} style={{ backgroundColor: 'transparent' }}>
      <Table className={classes.table}>
        <TableHead style={{ background: colors.grey[300], height: '40px' }}>
          <TableRow>
            <TableCell
              className={classes.headerTypeCell}
              style={{ textAlign: 'center' }}
            >
              QTY
            </TableCell>
            <TableCell className={classes.headerTypeCell}>TYPE</TableCell>
            <TableCell className={classes.headerTypeCell}>
              PRODUCT NAME
            </TableCell>
            <TableCell className={classes.headerTypeCell}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: capturingScreen ? 'end' : 'start',
                  alignItems: 'center'
                }}
              >
                PRICE{' '}
              </Box>
            </TableCell>
            <TableCell
              className={classes.headerTypeCell}
              align={capturingScreen ? 'right' : 'left'}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: capturingScreen ? 'end' : 'start',
                  alignItems: 'center'
                }}
              >
                {capturingScreen ? (
                  <img
                    src="/static/images/icons/srp.png"
                    alt="SRP"
                    width={25}
                    height={25}
                    style={{ paddingRight: '2.5px' }}
                  />
                ) : null}
                TOTAL
              </Box>
            </TableCell>
            {isScreenCapture ? null : (
              <TableCell style={{ width: '3%', textAlign: 'end' }}></TableCell>
            )}
            {isScreenCapture ? null : (
              <TableCell
                style={{
                  width: '3%',
                  textAlign: 'end'
                }}
              ></TableCell>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {quotationData?.map((quotation, index) => (
            <TableRow key={index} style={{ padding: 0 }}>
              <QuotationQuantityCell
                index={index}
                isScreenCapture={isScreenCapture}
                category={{
                  category_id: quotation?.product?.category_id,
                  category_name: quotation?.product?.category_name
                }}
                quotation={quotation}
              />
              <QuotationTypeCell
                index={index}
                isScreenCapture={isScreenCapture}
                category={{
                  category_id: quotation?.product?.category_id,
                  category_name: quotation?.product?.category_name
                }}
              />
              <QuotationProductNameCell
                index={index}
                isScreenCapture={isScreenCapture}
                quotation={quotation}
                category={{
                  category_id: quotation?.product?.category_id,
                  category_name: quotation?.product?.category_name
                }}
              />
              <QuotationPriceCell
                index={index}
                quotation={quotationData}
                retail_price={
                  quotation.product?.retail_price
                    ? +(quotation.product.retail_price * 1.12).toFixed(2)
                    : 0
                }
                promo={
                  quotation.product?.retail_price
                    ? quotation.product?.retail_price
                    : 0
                }
                onChangeCustomPrice={(index, newPrice) =>
                  onChangeCustomPrice(index, quotationData, newPrice)
                }
                is_srp={quotationParams?.is_srp}
                isScreenCapture={isScreenCapture}
              />
              <QuotationTotalCell
                quotation={quotation}
                isScreenCapture={isScreenCapture}
              />

              {isScreenCapture ? null : (
                <TableCell style={{ padding: 0 }}>
                  <MenuItemQuote index={index} quotation={quotation} />
                </TableCell>
              )}
              {isScreenCapture ? null : (
                <TableCell style={{ width: '10px', padding: 0 }}>
                  {quotation?.warning_note ? (
                    <Tooltip title={quotation?.warning_note}>
                      <ReportProblemIcon color="secondary" />
                    </Tooltip>
                  ) : null}
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <QuotationTotalPrice
        isScreenCapture={isScreenCapture}
        is_srp={quotationParams?.is_srp}
      />
    </Card>
  );
};

export const QuotationV2Body = memo(Component);
